/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CardContent,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
  Stack,
  Fade,
  LinearProgress,
  Chip,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import Grid from "@mui/material/Grid2";
import {
  ArrowUpward,
  ArrowDownward,
  Add,
  Save,
  Delete,
  Close,
} from "@mui/icons-material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import Toast from "@/components/toast";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import useEditData from "@/hooks/editHooks/useEditData";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const ChecklistForm = ({
  open,
  onClose,
  checklist = null,
  url = "/api/v1/org-admin/checklists",
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    itemIndex: null,
    itemName: "",
  });
  const [formValid, setFormValid] = useState(false);

  const { mutateAsync: postData, isLoading: isPosting } = usePostJsonData();
  const { mutateAsync: editData, isLoading: isEditing } = useEditData(
    checklist ? `${url}/${checklist.id}` : url,
    ["checklists"]
  );

  useEffect(() => {
    if (checklist) {
      setName(checklist.name || "");
      setDescription(checklist.description || "");
      setItems(
        checklist.items
          ? checklist.items.map((item, index) => ({
              text: item.text || "",
              description: item.description || "",
              is_section: item.is_section || false,
              position: index + 1,
            }))
          : []
      );
    } else {
      setName("");
      setDescription("");
      setItems([]);
    }
  }, [checklist]);

  // Form validation
  useEffect(() => {
    const nameValid = name.trim().length >= 2;
    const descriptionValid = description.trim().length > 0;
    const itemsValid =
      items.length === 0 || items.every((item) => item.text.trim().length > 0);

    setFormValid(nameValid && descriptionValid && itemsValid);
  }, [name, description, items]);

  // Early validation feedback for name field
  const nameError =
    name.length > 0 && name.trim().length < 2
      ? t("checklistForm.nameError")
      : "";

  // Early validation feedback for description field
  const descriptionError =
    description.length > 0 && description.trim().length === 0
      ? t("checklistForm.descriptionError")
      : "";

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        text: "",
        description: "",
        is_section: false,
        position: prev.length + 1,
      },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleDeleteItemClick = (index) => {
    const item = items[index];
    setDeleteConfirmation({
      open: true,
      itemIndex: index,
      itemName: item.text || `${t("checklistForm.item")} ${index + 1}`,
    });
  };

  const confirmDeleteItem = () => {
    const { itemIndex } = deleteConfirmation;
    setItems((prev) => {
      const newItems = prev.filter((_, i) => i !== itemIndex);
      return newItems.map((item, i) => ({
        ...item,
        position: i + 1,
      }));
    });
    setDeleteConfirmation({ open: false, itemIndex: null, itemName: "" });
  };

  const cancelDeleteItem = () => {
    setDeleteConfirmation({ open: false, itemIndex: null, itemName: "" });
  };

  const moveItem = (index, direction) => {
    const newItems = [...items];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [
      newItems[targetIndex],
      newItems[index],
    ];

    newItems.forEach((item, i) => {
      item.position = i + 1;
    });

    setItems(newItems);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;

    const payload = { name, description, items };

    try {
      await postData({ url, data: payload });
      setToast({
        open: true,
        message: t("checklistForm.createSuccessToast"),
        severity: "success",
      });
      queryClient.invalidateQueries(["checklists"]);
      setName("");
      setDescription("");
      setItems([]);
      onClose();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) return;

    const payload = { name, description, items };

    try {
      await editData(payload);
      setToast({
        open: true,
        message: t("checklistForm.updateSuccessToast"),
        severity: "success",
      });
      queryClient.invalidateQueries(["checklists"]);
      setName("");
      setDescription("");
      setItems([]);
      onClose();
    } catch (err) {
      handleApiError(err);
    }
  };

  const getProgressValue = () => {
    const nameValid = name.trim().length >= 2;
    const itemsValid =
      items.length === 0 || items.every((item) => item.text.trim().length > 0);
    let progress = 0;
    if (nameValid) progress += 50;
    if (itemsValid) progress += 50;
    return progress;
  };

  const sectionCount = items.filter((item) => item.is_section).length;
  const questionCount = items.filter((item) => !item.is_section).length;

  return (
    <>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            maxHeight: "90vh",
            background: theme.palette.background.paper,
          },
        }}
        aria-labelledby="checklist-form-dialog-title"
      >
        <DialogTitle
          id="checklist-form-dialog-title"
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                {checklist
                  ? t("checklistForm.titleEdit")
                  : t("checklistForm.titleCreate")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {checklist
                  ? t("checklistForm.subtitleEdit")
                  : t("checklistForm.subtitleCreate")}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                bgcolor: alpha(theme.palette.text.primary, 0.08),
                "&:hover": {
                  bgcolor: alpha(theme.palette.text.primary, 0.12),
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              aria-label={t("checklistForm.closeDialogAria")}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("checklistForm.progressLabel")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getProgressValue()}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={getProgressValue()}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: "background.default",
            minHeight: 400,
          }}
        >
          {/* Basic Information Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t("checklistForm.basicInfoTitle")}
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium", mb: 1 }}
                >
                  {t("checklistForm.nameLabel")} *
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("checklistForm.nameDescription")}
                </Typography>
                <TextField
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder={t("checklistForm.namePlaceholder")}
                  error={!!nameError}
                  helperText={nameError}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      minHeight: 48,
                      background: theme.palette.background.paper,
                    },
                  }}
                  aria-label={t("checklistForm.nameAria")}
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium", mb: 1 }}
                >
                  {t("checklistForm.descriptionLabel")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("checklistForm.descriptionDescription")}
                </Typography>
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder={t("checklistForm.descriptionPlaceholder")}
                  error={!!descriptionError}
                  helperText={descriptionError}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      background: theme.palette.background.paper,
                    },
                  }}
                  aria-label={t("checklistForm.descriptionAria")}
                />
              </Box>
            </Stack>
          </Paper>

          {/* Items Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.secondary.main, 0.02),
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {t("checklistForm.itemsTitle")}
                  {items.length > 0 && (
                    <Chip
                      label={items.length}
                      size="small"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("checklistForm.itemsDescription")}
                </Typography>
                {(sectionCount > 0 || questionCount > 0) && (
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    {sectionCount > 0 && (
                      <Chip
                        label={t("checklistForm.sectionsCount", {
                          count: sectionCount,
                        })}
                        size="small"
                        variant="outlined"
                        sx={{
                          bgcolor: alpha(theme.palette.secondary.main, 0.1),
                          color: theme.palette.secondary.main,
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {questionCount > 0 && (
                      <Chip
                        label={t("checklistForm.questionsCount", {
                          count: questionCount,
                        })}
                        size="small"
                        variant="outlined"
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                          borderColor: alpha(theme.palette.info.main, 0.2),
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {items.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  border: `2px dashed ${alpha(theme.palette.grey[500], 0.3)}`,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                  mb: 3,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {t("checklistForm.noItemsMessage")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                  }}
                >
                  {t("checklistForm.noItemsInfo")}
                </Typography>
              </Paper>
            )}

            <Grid container spacing={2}>
              <Grid size={12}>
                <Stack spacing={2}>
                  {items.map((item, idx) => (
                    <Fade in key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 3,
                          p: 2,
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: item.is_section
                            ? alpha(theme.palette.secondary.main, 0.2)
                            : alpha(theme.palette.info.main, 0.2),
                          bgcolor: item.is_section
                            ? alpha(theme.palette.secondary.main, 0.03)
                            : alpha(theme.palette.info.main, 0.03),
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: item.is_section
                              ? alpha(theme.palette.secondary.main, 0.4)
                              : alpha(theme.palette.info.main, 0.4),
                            boxShadow: theme.shadows[4],
                          },
                        }}
                        aria-label={t("checklistForm.itemAria", {
                          index: idx + 1,
                        })}
                      >
                        <CardContent>
                          <Stack spacing={2}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {t("checklistForm.itemLabel", {
                                    index: idx + 1,
                                  })}
                                </Typography>
                                {item.is_section && (
                                  <Chip
                                    label={t("checklistForm.sectionBadge")}
                                    size="small"
                                    sx={{
                                      bgcolor: theme.palette.secondary.main,
                                      color: theme.palette.common.white,
                                      fontWeight: 600,
                                      boxShadow: theme.shadows[1],
                                    }}
                                    variant="filled"
                                  />
                                )}
                              </Box>
                              <Box>
                                <IconButton
                                  onClick={() => moveItem(idx, "up")}
                                  disabled={idx === 0}
                                  size="small"
                                  sx={{
                                    color:
                                      idx === 0
                                        ? "text.disabled"
                                        : "primary.main",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                      ),
                                    },
                                  }}
                                  aria-label={t("checklistForm.moveUpAria", {
                                    index: idx + 1,
                                  })}
                                >
                                  <ArrowUpward />
                                </IconButton>
                                <IconButton
                                  onClick={() => moveItem(idx, "down")}
                                  disabled={idx === items.length - 1}
                                  size="small"
                                  sx={{
                                    color:
                                      idx === items.length - 1
                                        ? "text.disabled"
                                        : "primary.main",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                      ),
                                    },
                                  }}
                                  aria-label={t("checklistForm.moveDownAria", {
                                    index: idx + 1,
                                  })}
                                >
                                  <ArrowDownward />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleDeleteItemClick(idx)}
                                  size="small"
                                  sx={{
                                    color: "error.main",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.error.main,
                                        0.08
                                      ),
                                    },
                                  }}
                                  aria-label={t(
                                    "checklistForm.deleteItemAria",
                                    {
                                      index: idx + 1,
                                    }
                                  )}
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </Box>
                            <TextField
                              label={t("checklistForm.itemTextLabel")}
                              value={item.text}
                              onChange={(e) =>
                                handleItemChange(idx, "text", e.target.value)
                              }
                              fullWidth
                              variant="outlined"
                              error={
                                item.text.length > 0 &&
                                item.text.trim().length === 0
                              }
                              helperText={
                                item.text.length > 0 &&
                                item.text.trim().length === 0
                                  ? t("checklistForm.itemTextError")
                                  : ""
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1,
                                  background: theme.palette.background.paper,
                                },
                              }}
                              aria-label={t("checklistForm.itemTextAria", {
                                index: idx + 1,
                              })}
                            />
                            <TextField
                              label={t("checklistForm.itemDescriptionLabel")}
                              value={item.description}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "description",
                                  e.target.value
                                )
                              }
                              fullWidth
                              variant="outlined"
                              multiline
                              rows={2}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1,
                                  background: theme.palette.background.paper,
                                },
                              }}
                              aria-label={t(
                                "checklistForm.itemDescriptionAria",
                                {
                                  index: idx + 1,
                                }
                              )}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.is_section}
                                  onChange={(e) =>
                                    handleItemChange(
                                      idx,
                                      "is_section",
                                      e.target.checked
                                    )
                                  }
                                  sx={{
                                    color: item.is_section
                                      ? theme.palette.secondary.main
                                      : theme.palette.info.main,
                                  }}
                                  aria-label={t(
                                    "checklistForm.sectionCheckboxAria",
                                    { index: idx + 1 }
                                  )}
                                />
                              }
                              label={
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: "medium" }}
                                  >
                                    {t("checklistForm.sectionCheckboxLabel")}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t(
                                      "checklistForm.sectionCheckboxDescription"
                                    )}
                                  </Typography>
                                </Box>
                              }
                              sx={{ color: theme.palette.text.secondary }}
                            />
                          </Stack>
                        </CardContent>
                      </Paper>
                    </Fade>
                  ))}
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={handleAddItem}
                startIcon={<Add />}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  borderWidth: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.primary.dark,
                    borderWidth: 2,
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[4],
                  },
                  transition: "all 0.2s ease",
                }}
                aria-label={t("checklistForm.addItemAria")}
              >
                {t("checklistForm.addItemButton")}
              </Button>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Form Status */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: formValid ? "success.main" : "warning.main",
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {formValid
                  ? t("checklistForm.formReady")
                  : t("checklistForm.formIncomplete")}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={checklist ? handleEditSubmit : handleCreateSubmit}
              disabled={isPosting || isEditing || !formValid}
              startIcon={<Save />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: "bold",
                boxShadow: 2,
                "&:hover": {
                  background: theme.palette.primary.dark,
                  boxShadow: 4,
                  transform: "translateY(-1px)",
                },
              }}
              aria-label={
                checklist
                  ? t("checklistForm.updateButtonAria")
                  : t("checklistForm.saveButtonAria")
              }
            >
              {isPosting || isEditing
                ? t("checklistForm.submittingText")
                : checklist
                  ? t("checklistForm.updateButton")
                  : t("checklistForm.saveButton")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={cancelDeleteItem}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
            pt: 3,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "error.contrastText",
              }}
            >
              <DeleteForeverOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {t("checklistForm.deleteItemTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("checklistForm.deleteItemSubtitle")}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: alpha(theme.palette.error.main, 0.02),
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("checklistForm.deleteItemMessage", {
              itemName: deleteConfirmation.itemName,
            })}
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.2),
            }}
          >
            <Typography
              variant="body2"
              color="error.main"
              sx={{ fontWeight: "medium" }}
            >
              ⚠️ {t("checklistForm.deleteItemWarning")}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            bgcolor: alpha(theme.palette.error.main, 0.02),
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={cancelDeleteItem}
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "medium",
            }}
          >
            {t("checklistForm.cancelDelete")}
          </Button>
          <Button
            onClick={confirmDeleteItem}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
          >
            {t("checklistForm.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChecklistForm;
